import React, { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import cn from 'classnames'
import AttributeSelector from '../components/AttributeSelector'
import Selector from '../components/Selector'
import AttributeSelectorMobile from '../components/AttributeSelectorMobile'
import { useSetUrlParams, parseUrlParams } from '../hooks/useUrlParams'
import { useJsonQuery } from '../hooks/useQuery'

function ProductsIndex(props) {
  const htmlContainer = useRef(document.querySelectorAll('[data-products-index]').item(0))
  const { queryUrl, summaryUrl, categories = [], categorySlug: preFilteredCategorySlug } = props

  const [mobileShowFilters, setMobileShowFilters] = useState(false)
  const [mobileCategorySelectorOpen, setMobileCategorySelectorOpen] = useState(false)

  const [selectedCategorySlug, setSelectedCategorySlug] = useState(
    preFilteredCategorySlug || parseUrlParams().category || null
  )
  const selectedCategory = categories.find((c) => c.slug === selectedCategorySlug)
  const categoryFilterGroups = selectedCategory ? selectedCategory.filter_groups || [] : []
  const hasFilters = categoryFilterGroups.length > 0

  const [attributeValues, setAttributeValues] = useState(parseUrlParams(['page', 'category']))

  const currentCategoryAttributeValues = categoryFilterGroups.reduce((acc, filterGroup) => {
    if (filterGroup.slug in attributeValues) return { ...acc, [filterGroup.slug]: attributeValues[filterGroup.slug] }
    return acc
  }, {})
  const currentCategoryAttributeValueKeys = Object.keys(currentCategoryAttributeValues)

  const urlParams = {
    category: selectedCategory ? selectedCategory.slug : undefined,
    ...currentCategoryAttributeValues,
  }
  const paramsStr = useSetUrlParams(urlParams, ['page'])

  // const [{products}, { loading, loaded }] = useJsonQuery(paramsStr ? `/products.json?${paramsStr}` : null, {products: []})
  const [{ pagination, html }, { loading, loaded }] = useJsonQuery(`${queryUrl}${paramsStr ? `?${paramsStr}` : ''}`, {
    pagination: {},
    html: null,
  })

  /**
    pagination: {
      "count": 5,
      "current": 5,
      "perPage": 100,
      "page": 1,
      "requestedPage": 1,
      "pageCount": 1,
      "start": 1,
      "end": 5,
      "prevPage": false,
      "nextPage": false,
      "sort": null,
      "direction": null,
      "sortDefault": false,
      "directionDefault": false,
      "completeSort": [],
      "limit": null,
      "scope": null,
      "finder": "all"
    }
   */
  const initialFetchOccurred = useRef(false)

  useEffect(() => {
    if (html !== null) {
      if (!initialFetchOccurred.current) {
        console.log('ignoring initial fetch')
        initialFetchOccurred.current = true
        return
      }
      if (window.unmountDraggableAttributes) window.unmountDraggableAttributes()
      htmlContainer.current.innerHTML = html
      if (window.mountDraggableAttributes) window.mountDraggableAttributes()
      console.log(
        pagination.page < pagination.pageCount ? 'there are more pages to load' : 'all results contained on this page'
      )
    }
  }, [html])

  useEffect(() => {
    if (loaded || !loading) {
      htmlContainer.current.classList.remove('loading')
    } else if (loading) {
      htmlContainer.current.classList.add('loading')
    }
  }, [loading])

  useEffect(() => {
    const elem = document.getElementsByClassName('products-filter-spacer').item(0)
    if (!elem) return
    const filterVisible = selectedCategory && hasFilters
    if (filterVisible && !elem.classList.contains('filters-visible')) elem.classList.add('filters-visible')
    if (!filterVisible && elem.classList.contains('filters-visible')) elem.classList.remove('filters-visible')
    if (preFilteredCategorySlug && elem.classList.contains('categories-visible'))
      elem.classList.remove('categories-visible')
    if (!preFilteredCategorySlug && !elem.classList.contains('categories-visible'))
      elem.classList.add('categories-visible')
  }, [selectedCategory, hasFilters, preFilteredCategorySlug])

  useUpdateSummaryHtml(summaryUrl, selectedCategory ? selectedCategory.slug : undefined, initialFetchOccurred.current)

  return (
    <>
      <section className={cn('products-index-section container')}>
        <div className={cn('relative')}>
          <nav
            data-sticky
            className={cn(
              'hide-xs',
              'products-filter',
              'products-filter-desktop',
              'width-100 padding-xs',
              'flex flex-column align-items-center gap-y-xs',
              'background-white',
              'border-radius',
              'box-shadow-m'
            )}>
            {!preFilteredCategorySlug && (
              <div
                className={cn(
                  'categories',
                  'width-1',
                  'flex flex-wrap gap-x-xxs align-items-center justify-content-space-around'
                )}>
                <button
                  type="button"
                  className={cn(
                    'button button-text button-s hover:color-primary',
                    !selectedCategory && 'button-primary'
                  )}
                  onClick={() => setSelectedCategorySlug(null)}
                  children="All Products"
                />
                {categories.map((category) => (
                  <button
                    key={category.slug}
                    type="button"
                    className={cn(
                      'button button-text button-s hover:color-primary',
                      selectedCategorySlug === category.slug && 'button-primary'
                    )}
                    onClick={() => setSelectedCategorySlug(category.slug)}
                    children={category.short_title || category.title}
                  />
                ))}
              </div>
            )}
            {hasFilters && (
              <div className={cn('flex flex-wrap gap-xxs align-items-center justify-content-flex-start')}>
                {categoryFilterGroups.map((filterGroup) => (
                  <AttributeSelector
                    key={filterGroup.id}
                    attributes={filterGroup.categories.map((category) => ({
                      ...category,
                      label: category.short_title || category.title,
                    }))}
                    empty={filterGroup.short_title || filterGroup.title}
                    value={attributeValues[filterGroup.slug]}
                    onChange={(attrVal) => setAttributeValues((v) => ({ ...v, [filterGroup.slug]: attrVal }))}
                  />
                ))}
                <button
                  type="button"
                  className="button button-s button-text font-size-xxs"
                  onClick={() => setAttributeValues({})}
                  children="Clear Filters"
                />
              </div>
            )}
          </nav>
          <nav
            data-sticky
            className={cn(
              'hide-s-up',
              'products-filter',
              'products-filter-mobile',
              hasFilters && 'has-filters',
              'width-100',
              'flex flex-column',
              'background-white',
              'border-radius',
              'box-shadow-m'
            )}>
            <div className={cn('category-selector')}>
              <Selector
                empty="All Products"
                options={categories.map((category) => ({ ...category, label: category.short_title || category.title }))}
                keyField="slug"
                value={selectedCategorySlug}
                onChange={setSelectedCategorySlug}
                onOpenChange={setMobileCategorySelectorOpen}
              />
            </div>
            {selectedCategory && hasFilters && (
              <div
                className={cn(
                  'filters-container',
                  'border',
                  'border-color-light',
                  'border-radius',
                  'background-white',
                  mobileShowFilters && 'box-shadow-m'
                )}>
                {mobileShowFilters && !mobileCategorySelectorOpen && (
                  <div className={cn('padding-s')}>
                    {categoryFilterGroups.map((filterGroup) => (
                      <AttributeSelectorMobile
                        key={filterGroup.id}
                        attributes={filterGroup.categories.map((category) => ({
                          ...category,
                          label: category.short_title || category.title,
                        }))}
                        empty={filterGroup.short_title || filterGroup.title}
                        value={attributeValues[filterGroup.slug]}
                        onChange={(attrVal) => setAttributeValues((v) => ({ ...v, [filterGroup.slug]: attrVal }))}
                      />
                    ))}
                    <button
                      type="button"
                      className="button button-s button-text font-size-xxs"
                      onClick={() => {
                        setAttributeValues({})
                        setMobileShowFilters(false)
                      }}
                      children="Clear Filters"
                    />
                  </div>
                )}
                <div
                  className={cn(
                    'flex justify-content-space-between align-items-center',
                    'padding-vertical-xxs padding-horizontal-xs',
                    mobileShowFilters && 'border-top border-color-light'
                  )}
                  onClick={() => setMobileShowFilters((v) => !v)}>
                  <button type="button" className="button button-xs button-text button-icon">
                    <span>{mobileShowFilters ? 'Hide Filters' : 'Show Filters'}</span>
                    <svg width="24" height="24">
                      <use xlinkHref={`/assets/glyphs/glyphs.svg#${mobileShowFilters ? 'minus' : 'plus'}`} />
                    </svg>
                  </button>
                  <div
                    className={cn('filter-indicator')}
                    style={{ opacity: !mobileShowFilters && currentCategoryAttributeValueKeys.length > 0 ? 1 : 0 }}>
                    {currentCategoryAttributeValueKeys.length}
                  </div>
                </div>
              </div>
            )}
          </nav>
        </div>
      </section>
    </>
  )
}

/**
 * A hook that updates the title and summary section of the page with the HTML returned from the
 * summaryUrl endpoint, but only if the page url has changed.
 * If the categorySlug is provided, it will be passed as a query parameter to the summaryUrl.
 * If summaryUrl is not provided, the summary section will not be updated.
 */
function useUpdateSummaryHtml(summaryUrl, categorySlug) {
  const currentUrl = window.location.href
  const [initialUrl] = useState(currentUrl)
  const urlHasChangedRef = useRef(false)

  useEffect(() => {
    if (currentUrl !== initialUrl) {
      urlHasChangedRef.current = true
    }
  }, [currentUrl])

  const [{ html, title }] = useJsonQuery(
    summaryUrl && urlHasChangedRef.current ? `${summaryUrl}?category=${categorySlug || ''}` : false,
    { html: null, title: null }
  )

  useEffect(() => {
    if (html !== null) {
      const summaryContainer = document.querySelector('section.summary-section')
      if (summaryContainer) summaryContainer.outerHTML = html
    }
  }, [html])

  useEffect(() => {
    if (title) document.title = title
  }, [title])
}

window.ProductsIndex = {
  init: (id, props) => {
    if (!props.categories) console.warn('No categories fam...')
    ReactDOM.render(<ProductsIndex {...props} />, document.getElementById(id))
  },
}
